// SPDX-License-Identifier: MIT
/**
 * node-field (scale, 5–7 beats; reviewed in the Kestrel reel). A tilted plane of nodes under a
 * slowly orbiting camera. On every beat a wave of activation ripples out from a different node;
 * from mid-scene lit neighbours link up and arcs travel between distant nodes, and a final sweep
 * lights the whole field. A counter rises to the headline number top-left.
 */
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import * as kit from '../kit/index.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { Camera } from '../three.ts';
import { fitLine, sceneBeats } from './lib.ts';
const COLS = 30;
const ROWS = 14;
const SPACING = 120;
/** "3万+" → { prefix: "", n: 3, suffix: "万+" }; "12,000" keeps its separators when counting. */
export function parseCount(value: string): {
    prefix: string;
    n: number;
    suffix: string;
    decimals: number;
    grouped: boolean;
} | null {
    const m = /^(\D*?)(\d[\d,]*(?:\.\d+)?)(.*)$/.exec(value.trim());
    if (!m)
        return null;
    const digits = m[2]!;
    return {
        prefix: m[1]!,
        n: Number(digits.replace(/,/g, '')),
        suffix: m[3]!,
        decimals: digits.includes('.') ? digits.split('.')[1]!.length : 0,
        grouped: digits.includes(','),
    };
}
function formatCount(c: NonNullable<ReturnType<typeof parseCount>>, k: number): string {
    const v = c.n * k;
    const s = c.grouped
        ? v.toLocaleString('en-US', { minimumFractionDigits: c.decimals, maximumFractionDigits: c.decimals })
        : v.toFixed(c.decimals);
    return c.prefix + s + c.suffix;
}
export default defineScene({
    id: 'node-field',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema: z.object({ value: z.string().check(z.maxLength(12)), label: z.string().check(z.maxLength(60)) }),
    glyphs: (p) => p.value + p.label + '0123456789,.',
    motion: () => 0.6,
    audioEvents: () => [0, 1, 2, 3, 4, 5].map((b) => ({ beat: b + 0.02, sfx: 'blip' as const, gain: 0.35 })),
    post: (_p, env) => {
        const L = sceneBeats(env);
        return env.style.background === 'dark'
            ? { bloom: 1.15 + 0.4 * kit.beat.seg(env, L - 1.2, L) }
            : { bloom: 0.5 };
    },
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const L = sceneBeats(env);
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        const yaw = -0.18 + 0.36 * kit.beat.seg(env, 0, L, kit.curves.silk);
        const cam = new Camera({ fov: 52, position: [0, -420, -300], pitch: 0.3, yaw });
        const horizon = cam.project([0, 0, 5200]);
        if (horizon)
            kit.fx.pool(ctx, DESIGN_W / 2, horizon.y + 40, 1100, th.primary, th.dark ? 0.12 : 0.05);
        const r = env.rng('waves');
        const waves = Math.max(1, Math.floor(L));
        const origins = Array.from({ length: waves + 1 }, () => [Math.floor(COLS * (0.3 + r() * 0.5)), Math.floor(r() * ROWS * 0.6)] as const);
        const node = (c: number, row: number) => [(c - (COLS - 1) / 2) * SPACING, 0, row * SPACING + 200] as [
            number,
            number,
            number
        ];
        const finale = kit.beat.seg(env, L - 1.4, L - 0.4, kit.curves.glide);
        const lit = (c: number, row: number) => {
            let v = 0;
            for (let b = 0; b < waves; b++) {
                const age = env.localBeat - b;
                if (age < 0 || age > 2.2)
                    continue;
                const [oc, or] = origins[b]!;
                const d = Math.hypot(c - oc, row - or);
                v = Math.max(v, Math.exp(-(((d - age * 6) / 1.9) ** 2)) * Math.exp(-age * 1.1));
            }
            const sweep = Math.exp(-(((ROWS - row - finale * (ROWS + 6)) / 2.5) ** 2));
            return Math.min(1, Math.max(v, finale > 0 ? sweep * 0.9 : 0, finale * 0.35));
        };
        const proj: Array<Array<{
            x: number;
            y: number;
            s: number;
            v: number;
        } | null>> = [];
        for (let c = 0; c < COLS; c++) {
            proj.push([]);
            for (let row = 0; row < ROWS; row++) {
                const p = cam.project(node(c, row));
                proj[c]!.push(p ? { x: p.x, y: p.y, s: p.scale, v: lit(c, row) } : null);
            }
        }
        const linkOn = kit.beat.seg(env, L * 0.25, L * 0.37);
        if (linkOn > 0) {
            ctx.lineWidth = 1.5;
            for (let c = 0; c < COLS; c++)
                for (let row = 0; row < ROWS; row++) {
                    const a = proj[c]![row];
                    if (!a)
                        continue;
                    for (const [dc, dr] of [
                        [1, 0],
                        [0, 1],
                    ] as const) {
                        const b = proj[c + dc]?.[row + dr];
                        if (!b)
                            continue;
                        const v = Math.min(a.v, b.v) * linkOn;
                        if (v < 0.08)
                            continue;
                        ctx.strokeStyle = rgba(th.primary, 0.7 * v);
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
        }
        ctx.save();
        for (let c = 0; c < COLS; c++)
            for (let row = 0; row < ROWS; row++) {
                const p = proj[c]![row];
                if (!p || p.x < -40 || p.x > DESIGN_W + 40)
                    continue;
                const size = (3 + 8 * p.v) * p.s * (th.dark ? 1.15 : 1.45);
                ctx.fillStyle =
                    p.v > 0.15
                        ? rgba(p.v > 0.7 ? th.hot : th.primary, 0.35 + 0.65 * p.v)
                        : rgba(th.fg, th.dark ? 0.3 : 0.42);
                ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
            }
        ctx.globalCompositeOperation = th.light;
        for (let c = 0; c < COLS; c++)
            for (let row = 0; row < ROWS; row++) {
                const p = proj[c]![row];
                if (p && p.v > 0.55)
                    kit.fx.pool(ctx, p.x, p.y, 44 * p.s, th.primary, (th.dark ? 0.5 : 0.25) * p.v);
            }
        ctx.restore();
        // Arcs between distant nodes in the second half (kept on the right two thirds).
        const arcs = kit.beat.seg(env, L * 0.5, L * 0.57);
        if (arcs > 0) {
            const rp = env.rng('packets');
            ctx.save();
            ctx.globalCompositeOperation = th.light;
            for (let i = 0; i < 9; i++) {
                const a = node(Math.floor(COLS * (0.4 + rp() * 0.6)), Math.floor(rp() * ROWS * 0.7));
                const b = node(Math.floor(COLS * (0.4 + rp() * 0.6)), Math.floor(rp() * ROWS * 0.7));
                const start = L * 0.5 + rp() * L * 0.4;
                const k = kit.beat.seg(env, start, start + 0.9, kit.curves.silk);
                if (k <= 0 || k >= 1)
                    continue;
                const pts: Array<[
                    number,
                    number
                ]> = [];
                for (let s = 0; s <= 24; s++) {
                    const u = Math.max(0, k - 0.35) + (s / 24) * Math.min(k, 0.35);
                    const p = cam.project([
                        a[0] + (b[0] - a[0]) * u,
                        -Math.sin(Math.PI * u) * 420,
                        a[2] + (b[2] - a[2]) * u,
                    ]);
                    if (p)
                        pts.push([p.x, p.y]);
                }
                if (pts.length < 2)
                    continue;
                ctx.strokeStyle = rgba(th.accent, 0.8 * arcs);
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(pts[0]![0], pts[0]![1]);
                for (const p of pts.slice(1))
                    ctx.lineTo(p[0], p[1]);
                ctx.stroke();
                const head = pts[pts.length - 1]!;
                kit.fx.pool(ctx, head[0], head[1], 30, th.accent, arcs);
            }
            ctx.restore();
        }
        // Counter, top-left over the sky, in the crisp layer.
        const inK = kit.beat.seg(env, 0, 0.5, kit.curves.glide);
        const count = parseCount(props.value);
        const shown = count
            ? formatCount(count, kit.beat.seg(env, 0, Math.min(2.4, L * 0.4), kit.curves.glide))
            : props.value;
        const px = fitLine(ctx, env.brand, props.value, 800, 150, 900);
        env.crisp((c) => {
            c.globalAlpha *= inK;
            c.textBaseline = 'alphabetic';
            c.font = font(env.brand, 800, px);
            c.letterSpacing = `${-px / 30}px`;
            c.fillStyle = th.fg;
            c.fillText(shown, 190, 112 + px);
            c.letterSpacing = '0px';
            c.font = font(env.brand, 500, 36);
            c.fillStyle = rgba(th.fg, 0.72);
            c.fillText(props.label, 194, 172 + px);
        });
    },
});
